import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, CheckCircle2, Shield, Clock, Zap, Send, MessageCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import API from '@/utils/api';

/**
 * Paywall — contextual, emotion-driven paywall
 * 
 * Props:
 *   context: 'publish' | 'lead_reply' | 'campaign' | 'general'
 *   leadName: name of lead (for lead_reply context)
 *   waitingCount: number of leads waiting (for lead context)
 */
export default function Paywall({ onClose, groupsCount, subscription, context = 'publish', leadName, waitingCount }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const hadTrial = subscription?.is_trial === true || subscription?.status === 'trial_expired';
  const trialExpired = subscription?.trial_expired || subscription?.trial_publications_exhausted;
  const showTrialOption = !hadTrial && !trialExpired;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin,
        action: 'publish',
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'Już wykorzystałeś okres próbny') {
        handleCheckout();
        return;
      }
      setLoading(false);
    }
  };

  const handleGoToPlans = () => {
    navigate('/panel/subskrypcja');
    onClose?.();
  };

  // Context-specific headers
  const getContextContent = () => {
    switch (context) {
      case 'lead_reply':
        return {
          icon: MessageCircle,
          title: leadName ? `${leadName} czeka na odpowiedź` : 'Masz nowego klienta',
          subtitle: 'Aby odpowiedzieć i zamknąć sprzedaż — aktywuj konto',
          urgencyText: waitingCount > 1 ? `${waitingCount} osób czeka na odpowiedź` : null,
        };
      case 'campaign':
        return {
          icon: Rocket,
          title: 'Ta kampania może przynieść Ci klientów',
          subtitle: 'Aktywuj konto, aby zacząć',
          urgencyText: null,
        };
      case 'publish':
        return {
          icon: Send,
          title: 'Twoja promocja jest gotowa',
          subtitle: 'Aktywuj konto i zacznij zdobywać klientów',
          urgencyText: groupsCount > 0 ? `${groupsCount} ${groupsCount === 1 ? 'grupa' : 'grup'} czeka na Twój post` : null,
        };
      default:
        return {
          icon: Zap,
          title: 'Wszystko jest gotowe — ale jeszcze nieaktywne',
          subtitle: 'Uruchom dostęp i zacznij zdobywać klientów',
          urgencyText: null,
        };
    }
  };

  const ctx = getContextContent();
  const CtxIcon = ctx.icon;

  // Trial expired → upgrade paywall
  if (hadTrial && trialExpired) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="paywall"
          >
            <div className="bg-gradient-to-br from-[#F47A3B] to-[#E06A2B] p-6 text-white text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center"
              >
                <CtxIcon className="w-8 h-8" />
              </motion.div>
              <h2 className="text-[22px] font-extrabold mb-1">{ctx.title}</h2>
              <p className="text-white/80 text-[14px]">{ctx.subtitle}</p>
            </div>

            <div className="p-6">
              {ctx.urgencyText && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 mb-4" data-testid="paywall-urgency">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <p className="text-[12px] font-bold text-red-700">{ctx.urgencyText}</p>
                </div>
              )}

              <div className="space-y-2 mb-5">
                {[
                  'System publikuje za Ciebie',
                  'Gotowe odpowiedzi na wiadomości',
                  'Zamienia zapytania w sprzedaż',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-[13px]">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-zinc-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-4 mb-5 text-center">
                <p className="text-[32px] font-extrabold text-zinc-900">149 <span className="text-[16px] font-bold text-zinc-500">zł / mies.</span></p>
                <div className="flex items-center justify-center gap-1.5 mt-1">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  <p className="text-[12px] text-emerald-600 font-medium">Pierwszych 5–15 zapytań zwykle już w pierwszym miesiącu</p>
                </div>
              </div>

              <Button
                onClick={handleGoToPlans}
                className="w-full bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25 h-auto"
                data-testid="paywall-checkout-btn"
              >
                Zacznij zdobywać klientów
              </Button>

              <button
                onClick={onClose}
                className="w-full mt-2 py-2 text-[12px] text-zinc-400 hover:text-zinc-600 transition-colors"
                data-testid="paywall-close"
              >
                Nie teraz
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // First time → trial option
  if (showTrialOption) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="paywall"
          >
            <div className="bg-gradient-to-br from-[#F47A3B] to-[#E06A2B] p-6 text-white text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center"
              >
                <CtxIcon className="w-8 h-8" />
              </motion.div>
              <h2 className="text-[22px] font-extrabold mb-1">{ctx.title}</h2>
              <p className="text-white/80 text-[14px]">Przetestuj system za darmo przez 7 dni</p>
            </div>

            <div className="p-6">
              {ctx.urgencyText && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 mb-4" data-testid="paywall-urgency">
                  <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  <p className="text-[12px] font-bold text-amber-700">{ctx.urgencyText}</p>
                </div>
              )}

              <div className="space-y-2 mb-5">
                {[
                  'System tworzy posty, które przyciągają klientów',
                  'Klienci trafiają do jednego panelu',
                  'Gotowe odpowiedzi na wiadomości',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-[13px]">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-zinc-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 mb-5 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-emerald-600" />
                  <p className="text-[14px] font-bold text-emerald-700">7 dni za darmo</p>
                </div>
                <p className="text-[12px] text-emerald-600">Bez karty. Bez zobowiązań. 3 publikacje.</p>
              </div>

              <Button
                onClick={handleStartTrial}
                disabled={loading}
                className="w-full bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25 h-auto"
                data-testid="paywall-trial-btn"
              >
                {loading ? 'Uruchamiam...' : 'Zacznij za darmo'}
              </Button>

              <button
                onClick={onClose}
                className="w-full mt-2 py-2 text-[12px] text-zinc-400 hover:text-zinc-600 transition-colors"
                data-testid="paywall-close"
              >
                Nie teraz
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Default: paid paywall
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', damping: 22, stiffness: 300 }}
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="paywall"
        >
          <div className="bg-gradient-to-br from-[#F47A3B] to-[#E06A2B] p-6 text-white text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center"
            >
              <CtxIcon className="w-8 h-8" />
            </motion.div>
            <h2 className="text-[22px] font-extrabold mb-1">{ctx.title}</h2>
            <p className="text-white/80 text-[14px]">{ctx.subtitle}</p>
          </div>

          <div className="p-6">
            {ctx.urgencyText && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 mb-4" data-testid="paywall-urgency">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <p className="text-[12px] font-bold text-red-700">{ctx.urgencyText}</p>
              </div>
            )}

            <div className="space-y-2 mb-5">
              {[
                'System publikuje za Ciebie',
                'Gotowe odpowiedzi na wiadomości',
                'Zamienia zapytania w sprzedaż',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-[13px]">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-zinc-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-4 mb-5 text-center">
              <p className="text-[32px] font-extrabold text-zinc-900">149 <span className="text-[16px] font-bold text-zinc-500">zł / mies.</span></p>
              <div className="flex items-center justify-center gap-1.5 mt-1">
                <Zap className="w-3.5 h-3.5 text-[#F47A3B]" />
                <p className="text-[12px] text-[#F47A3B] font-medium">Średnio 5–15 zapytań / miesiąc</p>
              </div>
            </div>

            <Button
              onClick={handleGoToPlans}
              className="w-full bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25 h-auto"
              data-testid="paywall-checkout-btn"
            >
              Zacznij zdobywać klientów
            </Button>

            <button
              onClick={onClose}
              className="w-full mt-2 py-2 text-[12px] text-zinc-400 hover:text-zinc-600 transition-colors"
              data-testid="paywall-close"
            >
              Nie teraz
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
